import React, { useState } from 'react';
import { Plus, AlertTriangle } from 'lucide-react';
import { Button, Badge, Input, Card, CardHeader, CardTitle, CardContent, DataTable } from '@clinic-os/ui';
import type { Column } from '@clinic-os/ui';
import type { InventoryItem } from '@clinic-os/shared/types';
import { useInventory, useCreateInventoryItem } from '../../hooks/useInventory';

export function InventoryPage() {
  const [search, setSearch] = useState('');
  const { data: items = [], isLoading } = useInventory();
  const createItem = useCreateInventoryItem();

  const filtered = items.filter(
    (i) =>
      i.name.toLowerCase().includes(search.toLowerCase()) ||
      i.category?.toLowerCase().includes(search.toLowerCase()) ||
      i.sku?.toLowerCase().includes(search.toLowerCase())
  );

  const lowStock = items.filter((i) => i.quantity <= i.lowStockThreshold);

  const columns: Column<InventoryItem>[] = [
    { key: 'name', header: 'Item', render: (i) => <span className="font-medium">{i.name}</span> },
    { key: 'sku', header: 'SKU', render: (i) => <span className="font-mono text-xs text-slate-500">{i.sku ?? '—'}</span> },
    { key: 'category', header: 'Category', render: (i) => <Badge variant="outline">{i.category ?? 'Uncategorized'}</Badge> },
    {
      key: 'quantity',
      header: 'Qty',
      render: (i) => (
        <div className="flex items-center gap-2">
          <span className={`font-medium ${i.quantity <= i.lowStockThreshold ? 'text-red-700' : 'text-slate-700'}`}>
            {i.quantity}
          </span>
          <span className="text-slate-500 text-xs">{i.unit}</span>
          {i.quantity <= i.lowStockThreshold && (
            <AlertTriangle className="w-4 h-4 text-red-500" />
          )}
        </div>
      ),
    },
    { key: 'threshold', header: 'Reorder At', render: (i) => <span className="text-slate-500 text-sm">{i.lowStockThreshold}</span> },
    { key: 'cost', header: 'Unit Cost', render: (i) => <span className="text-slate-700">${i.costPerUnit?.toFixed(2) ?? '—'}</span> },
    {
      key: 'expiry',
      header: 'Expiry',
      render: (i) => {
        if (!i.expiryDate) return <span className="text-slate-500">—</span>;
        const expiry = new Date(i.expiryDate);
        const isExpiringSoon = expiry.getTime() - Date.now() < 90 * 86400000;
        return (
          <span className={`text-sm ${isExpiringSoon ? 'text-amber-700 font-medium' : 'text-slate-500'}`}>
            {expiry.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
          </span>
        );
      },
    },
  ];

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Inventory</h1>
          <p className="text-sm text-slate-500 mt-1">
            {items.length} items · {lowStock.length} need reordering
          </p>
        </div>
        <Button size="lg">
          <Plus className="w-4 h-4" />
          Add Item
        </Button>
      </div>

      {lowStock.length > 0 && (
        <div className="mb-6 p-4 rounded-lg bg-amber-50 border border-amber-200 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-700 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-amber-800">Low stock alert</p>
            <p className="text-xs text-amber-700">
              {lowStock.map((i) => i.name).join(', ')} — {lowStock.length} items need reordering
            </p>
          </div>
        </div>
      )}

      <div className="mb-4">
        <Input
          placeholder="Search inventory..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-sm"
        />
      </div>

      <DataTable
        label="Inventory"
        columns={columns}
        data={filtered}
        keyExtractor={(i) => i.id}
        emptyMessage={isLoading ? 'Loading...' : 'No inventory items'}
      />
    </div>
  );
}
